<template>
    <UModal
        v-model:open="open"
        :title="$t('changelog.title')"
        :ui="{ content: 'w-[calc(100%-2rem)] md:w-3/5 lg:w-2/5 max-w-none' }"
    >
        <template #body>
            <div class="flex flex-col gap-6">
                <!-- What is running: the console's own build and the API's, read live. -->
                <div class="rounded-md border border-default p-3">
                    <p class="pb-2 font-mono text-[11px] uppercase tracking-widest text-muted">{{ $t('changelog.running') }}</p>

                    <p class="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        <span><span class="text-muted">{{ $t('changelog.console') }}</span> {{ consoleVersion }}</span>
                        <span v-if="apiVersion"><span class="text-muted">{{ $t('changelog.api') }}</span> v{{ apiVersion }}</span>
                    </p>
                </div>

                <section v-for="entry in CHANGELOG" :key="entry.date" class="flex flex-col gap-2">
                    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h3 class="text-base font-semibold text-highlighted">{{ formatDay(entry.date) }}</h3>

                        <UBadge
                            v-if="entry.date > seenBefore"
                            :label="$t('changelog.new_badge')"
                            color="primary"
                            variant="subtle"
                            size="sm"
                        />
                    </div>

                    <p class="text-xs text-muted">{{ versionsLine(entry.versions) }}</p>

                    <ul class="flex list-disc flex-col gap-1.5 pl-5 text-sm text-default">
                        <li v-for="(line, index) in linesOf(entry)" :key="index">{{ line }}</li>
                    </ul>
                </section>
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { Locale } from '@/enums/locale'
    import { CHANGELOG, type IChangelogEntry, type IChangelogVersions } from '@/libs/changelog'
    import { formatDay } from '@/libs/dayjs'
    import { useFetchStore } from '@/stores/fetch'

    /*
     * Novosti: the console's version, the API's, and what changed (libs/changelog.ts). Opened
     * from the version line in the sidebar. `seen` is the newest date the reader had seen when
     * it opened — entries after it carry "Novo" for this one viewing.
     */
    const props = defineProps<{ seen: string }>()

    const open = defineModel<boolean>('open', { default: false })

    const { t, locale } = useI18n()
    const fetchStore    = useFetchStore()

    const consoleVersion = __APP_VERSION__
    const apiVersion     = ref('')

    /* Fixed when the dialog opens, so the badges do not vanish under the reader as it is marked seen. */
    const seenBefore = ref(props.seen)

    const ORDER: (keyof IChangelogVersions)[] = ['console', 'screen', 'guest', 'android', 'api']

    const versionsLine = (versions: IChangelogVersions): string => ORDER
        .filter((part) => versions[part])
        .map((part) => `${t(`changelog.${part}`)} ${versions[part]}`)
        .join(' · ')

    const english = computed((): boolean => locale.value === Locale.En)

    const linesOf = (entry: IChangelogEntry): string[] => english.value ? entry.en : entry.sl

    watch(open, async (isOpen) => {
        if (!isOpen) return

        seenBefore.value = props.seen

        if (apiVersion.value) return

        // tv-api's own version (docs/API.md §2), the same answer tv-display's footer shows.
        const { statusCode, data } = await fetchStore.fetch('/version').get().json<{ version: string }>()

        if (statusCode.value === 200 && data.value?.version) apiVersion.value = data.value.version
    })
</script>
